import Phaser from 'phaser';
import IsoPlugin, { IsoPhysics } from 'phaser3-plugin-isometric';

import { X_GRIDS, Y_GRIDS, TILE_SIZE } from '../configs/Battle';
import ColorKeys from '../constants/ColorKeys';
import EventKeys from '../constants/EventKeys';
import PluginKeys from '../constants/PluginKeys';
import SceneKeys from '../constants/SceneKeys';
import HeroList from '../mockData/HeroList';
import { getTargetHero } from '../helpers/Battle';
import {
  setActiveHeroTileColor,
  setAvailableMoveTilesColor,
  setAvailableAttackTilesColor,
} from '../helpers/Battle/Color';
import {
  spawnDebugLabel,
  spawnTiles,
  spawnTeamTable,
  spawnHeros,
} from '../helpers/Battle/Spawn';
import { initHeroTurnOrder, turnNextHero } from '../helpers/Battle/Order';
import {
  spawnAvailableMoveTiles,
  clearAvailableMoveTiles,
  preMoveHero,
  postMoveHero,
} from '../helpers/Battle/Command/Move';
import {
  spawnAvailableAttackTiles,
  clearAvailableAttackTiles,
  preAttackHero,
} from '../helpers/Battle/Command/Attack';

class Battle extends Phaser.Scene {
  tiles;

  heros;

  herosTurnOrder;

  activeHero;

  movingHero;

  deadHeros;

  isMovable;

  availableMoveTiles;

  availableAttackTiles;

  rexUI;

  commandButtons;

  debugLabel;

  constructor() {
    super({
      key: SceneKeys.Battle,
      mapAdd: {
        isoPlugin: PluginKeys.Iso,
        isoPhysics: PluginKeys.IsoPhysics,
      },
    });
  }

  init() {
    this.tiles = Array.from(Array(X_GRIDS), () => new Array(Y_GRIDS));
    this.heros = HeroList;
    this.deadHeros = [];
    this.availableMoveTiles = [];
    this.availableAttackTiles = [];
    this.isMovable = false;
  }

  preload() {
    this.load.scenePlugin({
      key: PluginKeys.IsoPlugin,
      url: IsoPlugin,
      sceneKey: PluginKeys.Iso,
    });

    this.load.scenePlugin({
      key: PluginKeys.IsoPhysicsPlugin,
      url: IsoPhysics,
      sceneKey: PluginKeys.IsoPhysics,
    });
  }

  create() {
    this.add.image(this.cameras.main.width / 2, this.cameras.main.height / 2, 'background');
    this.isoGroup = this.add.group();
    this.iso.projector.origin.setTo(0.5, 0.3);

    spawnDebugLabel.call(this);
    spawnTeamTable.call(this, HeroList
      .filter((hero) => hero.team === 'A')
      .map((hero) => ({
        name: hero.name,
        content: `${hero.baseHp}/${hero.baseHp}`,
      })),
    );
    spawnTiles.call(this, X_GRIDS, Y_GRIDS, TILE_SIZE);
    spawnHeros.call(this);
    initHeroTurnOrder.call(this);

    this.events.on(EventKeys.TurnNextHero, turnNextHero, this);
  }

  update() {
    if (this.movingHero) {
      postMoveHero.call(this);
    }
  }

  handlePointerOver() {
    this.setTint(ColorKeys.Grey);
  }

  handlePointerOut(scene) {
    this.clearTint();

    const { isoX: activeHeroIsoX, isoY: activeHeroIsoY } =
      scene.activeHero.isoSprite;
    if (this.isoX === activeHeroIsoX && this.isoY === activeHeroIsoY) {
      setActiveHeroTileColor.call(scene);
    }

    if (scene.availableMoveTiles.length) {
      setAvailableMoveTilesColor.call(scene);
    } else if (scene.availableAttackTiles.length) {
      setAvailableAttackTilesColor.call(scene);
    }
  }

  handlePointerDown(scene) {
    const selectedHero = getTargetHero.call(scene, this);
    const { activeHero, isMovable } = scene;

    if (selectedHero && selectedHero.id === activeHero.id) {
      if (isMovable) {
        if (scene.availableMoveTiles.length) {
          clearAvailableMoveTiles.call(scene);
        } else {
          spawnAvailableMoveTiles.call(scene);
        }
        return;
      }

      if (scene.availableAttackTiles.length) {
        clearAvailableAttackTiles.call(scene);
      } else {
        spawnAvailableAttackTiles.call(scene);
      }

      return;
    }

    if (scene.availableMoveTiles.length) {
      preMoveHero.call(scene, this.isoX, this.isoY);
    } else if (scene.availableAttackTiles.length) {
      preAttackHero.call(scene, this.isoX, this.isoY);
    }
  }
}

export default Battle;
